import type { ReactNode } from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FormBackHeader } from '@/components/form/form-back-header';
import { FormDeleteButton } from '@/components/form/form-delete-button';
import { FormSelect } from '@/components/form/form-select';
import { inventoryStatusOptions } from '../../inventory.constants';
import { Skeleton } from '@/components/ui/skeleton';

interface SmartSuggestionFormShellProps {
  /** Present when editing; drives the delete/reset footer and the heading. */
  id?: string;
  title: string;
  description: string;
  isLoading?: boolean;
  isSaving?: boolean;
  onSubmit: () => void;
  onReset: () => void;
  onDelete: () => void;
  children: ReactNode;
}

/**
 * Page chrome for the three Smart Suggestion form pages: back header, status
 * switch and save on top, one card of fields, reset/delete/save footer. Same
 * shape as the activity and add-on form pages so every form in Inventory
 * behaves identically.
 */
const SmartSuggestionFormShell = ({
  id,
  title,
  description,
  isLoading,
  isSaving,
  onSubmit,
  onReset,
  onDelete,
  children,
}: SmartSuggestionFormShellProps) => (
  <form className="space-y-6" onSubmit={onSubmit}>
    <div className="flex flex-col gap-6 px-6 py-2">
      <div className="flex items-center justify-between rounded-2xl">
        <FormBackHeader title="Go Back" />

        <Button
          variant="primary"
          size="sm"
          disabled={isSaving}
          loading={isSaving}
        >
          Save Changes
        </Button>
      </div>
    </div>

    <div className="space-y-8 rounded-2xl bg-neutral-100 p-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-0.5">
          <h5 className="text-base font-semibold">{title}</h5>
          <p className="text-text-tertiary text-xs">{description}</p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full md:col-span-2" />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {children}

            {/* Status sits with the other fields rather than up in the header:
                it is part of the record, not page chrome. A select and not the
                switch the other Inventory forms use, because these records
                have three states and Delete writes the third one — a switch
                showed a suspended record as "Active" and saved it back that
                way. */}
            <FormSelect
              name="status"
              label="Status"
              options={inventoryStatusOptions}
              optionKey="value"
              isSearchable={false}
            />
          </div>
        )}
      </div>
    </div>

    <div className="flex items-center justify-between gap-4 rounded-2xl bg-neutral-100 p-6">
      {id && (
        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="p-2"
            onClick={onReset}
          >
            <RotateCcw />
          </Button>
          <FormDeleteButton onClickDialogAction={onDelete} />
        </div>
      )}
      <Button
        variant="primary"
        size="sm"
        className="ml-auto"
        disabled={isSaving}
        loading={isSaving}
      >
        Save Changes
      </Button>
    </div>
  </form>
);

export { SmartSuggestionFormShell };
